import type { ReactNode } from 'react';

import type { TPodcastPlatformsElement } from '@shared/dream-components';
import { PodcastPlatformsElement } from '@shared/dream-components';
import { pickBy } from 'es-toolkit';
import { defaultTo, merge } from 'es-toolkit/compat';

import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import type { PodcastShowEpisodePageLoaderData } from '~/routes/podcast/s/$showSlug/$episodeSlug';

interface Props {
  node: TPodcastPlatformsElement;
  children: ReactNode | ReactNode[];
}

const PodcastPlatforms = ({ node, children }: Props) => {
  const { podcastEpisode } = useTypedLoaderData<PodcastShowEpisodePageLoaderData>();

  /**
   * Merge show platform links with episode platform links, preferring episode links for overlapping platforms (i.e. Apple, Spotify, YouTube).
   */
  const platformLinks = merge(
    {},
    defaultTo(podcastEpisode?.show?.platform_links, {}),
    pickBy(defaultTo(podcastEpisode?.platform_links, {}), (url) => Boolean(url)),
  );

  if (!platformLinks || Object.values(platformLinks).every((url) => !url)) {
    return null;
  }

  const elementWithData: TPodcastPlatformsElement = {
    ...node,
    attrs: {
      ...node.attrs,
      data: { platform_links: platformLinks },
    },
  };

  return (
    <PodcastPlatformsElement element={elementWithData} isLive>
      {children}
    </PodcastPlatformsElement>
  );
};

export default PodcastPlatforms;
